import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";
import type { IKaajBlogPostCardProps } from "./KaajBlogPostCard.props";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const publishedDateFormatter = new Intl.DateTimeFormat("en", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const formatPublishedDate = (publishedAt?: string) => {
  if (!publishedAt) return null;
  const date = new Date(publishedAt);
  return Number.isNaN(date.getTime()) ? publishedAt : publishedDateFormatter.format(date);
};

export const KaajBlogPostCard = ({
  className,
  post,
  priority = false,
  variant = "standard",
}: IKaajBlogPostCardProps) => {
  const featured = variant !== "standard";
  const prominent = variant === "featured";
  const imageSrc = post.image?.src ?? post.image?.url;
  const publishedDate = formatPublishedDate(post.publishedAt);

  return (
    <article
      className={cn(
        "group relative min-w-0",
        featured ? "h-full" : "flex h-full flex-col",
        className,
      )}
    >
      <div
        className={cn(
          "relative flex h-full min-w-0 overflow-hidden border transition-[border-color,box-shadow,transform] duration-300 ease-out motion-reduce:transition-none",
          featured
            ? "min-h-[22rem] flex-col justify-end rounded-[2rem] border-[#D9E1FF] bg-[#080B1A] text-white shadow-[0_24px_70px_rgba(8,11,26,0.18)] hover:-translate-y-1 hover:shadow-[0_30px_84px_rgba(8,11,26,0.24)] motion-reduce:hover:translate-y-0"
            : "flex-1 flex-col rounded-[1.75rem] border-[#D9E1FF] bg-white shadow-[0_20px_55px_rgba(33,68,255,0.08)] hover:-translate-y-1 hover:border-[#AEBEFF] hover:shadow-[0_26px_70px_rgba(33,68,255,0.13)] motion-reduce:hover:translate-y-0",
        )}
      >
        {featured ? (
          <>
            {imageSrc ? (
              <Image
                alt={post.image?.alt ?? ""}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                height={post.image?.height}
                loading={priority ? "eager" : "lazy"}
                sizes={
                  prominent ? "(min-width: 1024px) 62vw, 100vw" : "(min-width: 1024px) 34vw, 100vw"
                }
                src={imageSrc}
                srcSet={post.image?.srcSet}
                width={post.image?.width}
              />
            ) : (
              <div aria-hidden="true" className="absolute inset-0 bg-[#132EDB]">
                <div className="absolute inset-x-[8%] top-[18%] h-px bg-white/30" />
                <div className="absolute inset-x-[14%] top-[36%] h-px bg-white/20" />
                <div className="absolute right-[12%] top-[12%] h-24 w-24 rounded-full border border-white/25" />
              </div>
            )}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,11,26,0.04)_8%,rgba(8,11,26,0.38)_48%,rgba(8,11,26,0.94)_100%)]"
            />
            <div className={cn("relative z-10", prominent ? "p-6 sm:p-8 lg:p-10" : "p-6 sm:p-7")}>
              <div className="mb-4 flex flex-wrap items-center gap-3 text-xs font-bold text-white/90">
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5">
                  Featured analysis
                </span>
                {publishedDate ? <time dateTime={post.publishedAt}>{publishedDate}</time> : null}
              </div>
              <h3
                className={cn(
                  "max-w-[18ch] text-balance font-extrabold leading-[1.05] tracking-[-0.035em]",
                  prominent ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl sm:text-3xl",
                )}
              >
                {post.title}
              </h3>
              {post.excerpt ? (
                <RichTextHtml
                  className={cn(
                    "mt-4 max-w-[60ch] text-pretty text-white/85 [&_p]:m-0",
                    prominent ? "text-base leading-7" : "text-sm leading-6",
                  )}
                  html={post.excerpt}
                />
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="relative aspect-[16/10] overflow-hidden border-b border-[#D9E1FF] bg-[#EEF2FF]">
              {imageSrc ? (
                <Image
                  alt={post.image?.alt ?? ""}
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  height={post.image?.height}
                  loading={priority ? "eager" : "lazy"}
                  sizes="(min-width: 1280px) 25vw, (min-width: 768px) 50vw, 100vw"
                  src={imageSrc}
                  srcSet={post.image?.srcSet}
                  width={post.image?.width}
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-[linear-gradient(135deg,#EEF2FF_0%,#FFFFFF_55%,#FFEAF9_100%)]"
                >
                  <div className="absolute inset-x-8 top-1/3 h-px bg-[#AEBEFF]" />
                  <div className="absolute bottom-8 left-8 h-10 w-10 rounded-full border border-[#2144FF]/30 bg-white" />
                  <div className="absolute bottom-10 right-8 h-2 w-16 rounded-full bg-[#55D6C2]" />
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col p-6 sm:p-7">
              {publishedDate ? (
                <time className="mb-4 text-xs font-bold text-[#4B5568]" dateTime={post.publishedAt}>
                  {publishedDate}
                </time>
              ) : null}
              <h3 className="text-balance text-2xl font-extrabold leading-tight tracking-[-0.03em] text-[#151927]">
                {post.title}
              </h3>
              {post.excerpt ? (
                <RichTextHtml
                  className="mt-4 text-pretty text-sm leading-7 text-[#4B5568] [&_p]:m-0"
                  html={post.excerpt}
                />
              ) : null}
              <span className="mt-auto inline-flex min-h-11 items-center pt-6 text-sm font-bold text-[#2144FF]">
                Read the analysis{" "}
                <span
                  aria-hidden="true"
                  className="ml-2 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                >
                  →
                </span>
              </span>
            </div>
          </>
        )}
        <Link
          className="absolute inset-0 z-20 rounded-[inherit] focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-[#2144FF]"
          href={post.href}
        >
          <span className="sr-only">Read {post.title}</span>
        </Link>
      </div>
    </article>
  );
};

KaajBlogPostCard.displayName = "Blog::KaajBlogPostCard";
